import { MetricName } from 'modules/campaigns/alerts/constants';
import { CampaignAlert } from 'modules/campaigns/alerts/types';
import {
  getComparator,
  getMetricName,
  getPreposition,
  getSuffix,
  getTimeUnits,
  parseHourDurationToFrequency,
} from 'modules/campaigns/alerts/utils';
import { SCHEDULED_CAMPAIGN_TYPE_FILTERS } from 'modules/campaigns/lib/constants';
import { CampaignType, CampaignTypeFilter } from 'modules/domains/campaign';

const getIsRateMetric = (metricName: MetricName) => metricName.slice(-4) === 'Rate';
const getVerb = (metricName: MetricName) => (getIsRateMetric(metricName) ? 'is' : 'are');
const getThresholdSuffix = (metricName: MetricName) => (getIsRateMetric(metricName) ? '%' : '');

const generateAlertDetailsList = ({
  alerts,
  isScheduled,
  campaignType,
}: {
  alerts: CampaignAlert[];
  isScheduled: boolean;
  campaignType: CampaignTypeFilter;
}) =>
  alerts?.map(({ comparisonOperator, frequency, threshold, metricName }) => {
    const metricDisplayName = getMetricName(metricName);
    const verb = getVerb(metricName);
    const comparatorDisplayText = getComparator(comparisonOperator);
    const thresholdSuffix = getThresholdSuffix(metricName);
    const preposition = getPreposition(isScheduled);
    const formattedFrequency = parseHourDurationToFrequency(frequency);
    const timeUnits = getTimeUnits(parseHourDurationToFrequency(frequency));
    const suffix = getSuffix(campaignType);

    return `${metricDisplayName} ${verb} ${comparatorDisplayText} ${threshold}${thresholdSuffix} ${preposition} ${formattedFrequency} ${timeUnits}${suffix}`;
  });

/**
 * Determines whether campaign or project level alerts should be
 * used as the data source for displaying alert-related UI in the
 * campaign editor/summary as well as journey send editors.
 * Returns campaign alerts if they exist or otherwise defaults
 * to project alerts.
 */
export const getAlertsToDisplay = (
  campaignAlerts: CampaignAlert[],
  projectCampaignAlerts: CampaignAlert[],
) => {
  const hasCampaignAlerts = campaignAlerts?.length;
  return hasCampaignAlerts ? campaignAlerts : projectCampaignAlerts;
};

/**
 * Returns list of formatted alert details for display
 */
export const getAlertDetails = (
  enabledAlerts: CampaignAlert[],
  campaignType: CampaignTypeFilter,
) => {
  const isScheduled = SCHEDULED_CAMPAIGN_TYPE_FILTERS.includes(campaignType);

  return generateAlertDetailsList({ alerts: enabledAlerts, isScheduled, campaignType });
};

export const getCampaignTypeFilterForAlert = (campaignType: CampaignTypeFilter | CampaignType) => {
  switch (campaignType) {
    case CampaignType.Blast:
      return CampaignTypeFilter.Blast;
    case CampaignType.Eligibility:
      return CampaignTypeFilter.Eligibility;
    default:
      /**
       * As a FYI, campaigns in published journeys are typed 'WorkflowTriggered'
       * in the backend and all other triggered campaigns are 'ApiTriggered'
       *
       * In campaign alerts land, triggered campaigns from both sources are just
       * classified as 'Triggered'
       */
      return CampaignTypeFilter.Triggered;
  }
};
